import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, TextButton } from '@xing-com/button';
import type { UploadApplication } from '@xing-com/crate-common-graphql-types';

import { DeleteVideoButton } from './components/delete-video-button/delete-video-button';
import { UploadFinished } from './components/upload/upload-finished';
import { VideoForm } from './components/video-form/video-form';
import { GetVideoDocument } from './graphql/queries/get-video-query.gql-types';
import { useUploadRequest } from './hooks/use-upload-request';
import type {
  DisplayValues,
  UpdateParams,
  VideoData,
} from './types/video-form';
import * as Styled from './video-upload.styles';

export type VideoFormUpdateProps = {
  application: UploadApplication;
  videoId: string;
  title: string;
  mediaId: string;
  onError?: (error: string | Error) => void;
  onDelete?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  onValueChange?: () => void;
  deleteVideoMutation: (mediaId: string) => Promise<void>;
  updateVideoMutation: (mediaId: string, videoData: VideoData) => Promise<void>;
};
export const VideoFormUpdate: React.FC<VideoFormUpdateProps> = ({
  title,
  application,
  videoId,
  mediaId,
  onError,
  onDelete,
  onSave,
  onCancel,
  onValueChange,
  deleteVideoMutation,
  updateVideoMutation,
}) => {
  const { $t } = useIntl();
  const uploadRequest = useUploadRequest();

  const [displayValues, setDisplayValues] = useState<DisplayValues>({});
  const [updates, setUpdate] = useState<UpdateParams>({});

  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const [titleError, setTitleError] = useState(false);

  const { data, loading, error } = useQuery(GetVideoDocument, {
    variables: {
      videoId,
    },
  });

  const updateValue = (
    type: string,
    value: File | string | undefined
  ): void => {
    if (typeof value !== 'undefined') {
      setUpdate({ ...updates, [type]: value });
      setDisplayValues({
        ...displayValues,
        [type]: typeof value === 'string' ? value : value?.name,
      });
      onValueChange?.();
    }
  };
  const handleDeleteVideo = async (): Promise<void> => {
    if (!mediaId) return;

    setIsDeleting(true);

    try {
      await deleteVideoMutation(mediaId);

      setIsDeleting(false);
      onDelete?.();
    } catch {
      setIsDeleting(false);
      onError?.($t({ id: 'VIDEO_DELETE_ERROR' }));
    }
  };
  const handleOnSave = async (): Promise<void> => {
    if (!mediaId) return;

    setIsUpdating(true);

    const videoData: VideoData = {};
    const subtitlesId = {
      en: data?.videoV2?.subtitles?.find((el) => el.language === 'en')?.id,
      de: data?.videoV2?.subtitles?.find((el) => el.language === 'de')?.id,
    };

    if (Object.keys(updates).length === 0) {
      onSave?.();
      return;
    }

    try {
      if (typeof updates?.title !== 'undefined') {
        videoData.title = updates?.title.trim();
      }

      // English subtitle
      if (
        typeof updates?.englishSubtitle !== 'undefined' &&
        (updates?.englishSubtitle !== null || subtitlesId.en)
      ) {
        const englishSubtitleUploadId = await uploadRequest({
          file: updates?.englishSubtitle,
          application,
        });
        videoData.englishSubtitle = {
          ...(subtitlesId.en ? { subtitleId: subtitlesId.en } : {}),
          filename: updates?.englishSubtitle?.name || null,
          uploadId: englishSubtitleUploadId,
        };
      }

      // German subtitle
      if (
        typeof updates?.germanSubtitle !== 'undefined' &&
        (updates?.germanSubtitle !== null || subtitlesId.de)
      ) {
        const germanSubtitleUploadId = await uploadRequest({
          file: updates?.germanSubtitle,
          application,
        });
        videoData.germanSubtitle = {
          ...(subtitlesId.de ? { subtitleId: subtitlesId.de } : {}),
          filename: updates?.germanSubtitle?.name || null,
          uploadId: germanSubtitleUploadId,
        };
      }

      // Preview Image
      if (
        typeof updates?.previewImage !== 'undefined' &&
        updates?.previewImage
      ) {
        const thumbnailUploadId = await uploadRequest({
          file: updates?.previewImage,
          application,
        });
        videoData.thumbnailUploadId = thumbnailUploadId;
      }

      await updateVideoMutation(mediaId, videoData);

      onSave?.();
    } catch (error) {
      onError?.(error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    const customThumbnails = data?.videoV2?.customThumbnails ?? [];
    const defaultThumbnails = data?.videoV2?.defaultThumbnails ?? [];

    const thumbnails =
      customThumbnails?.length > 0 ? customThumbnails : defaultThumbnails;

    const en = data?.videoV2?.subtitles?.find((el) => el.language === 'en');
    const de = data?.videoV2?.subtitles?.find((el) => el.language === 'de');

    setDisplayValues({
      title: title,
      englishSubtitle: en?.sources[0].source,
      germanSubtitle: de?.sources[0].source,
      previewImage: thumbnails?.[0]?.sources?.[0].source,
    });
  }, [data]);

  if (loading) {
    return null;
  }

  if (error || !data) {
    return null;
  }

  const isSaveButtonDisabled = isUpdating || isDeleting || titleError;

  return (
    <>
      <UploadFinished
        videoId={videoId}
        previewImage={updates.previewImage}
        onError={() => onError?.($t({ id: 'VIDEO_UPLOAD_ERROR' }))}
      />

      <VideoForm
        displayValues={displayValues}
        setTitleError={setTitleError}
        updateValue={updateValue}
        onError={(error) => onError?.(error)}
      />

      <Styled.CtaContainer>
        <DeleteVideoButton
          loading={isDeleting}
          onConfirmDialog={handleDeleteVideo}
        />
        <TextButton
          size="medium"
          disabled={isUpdating || isDeleting}
          onClick={onCancel}
        >
          <FormattedMessage id="VIDEO_UPLOAD_CANCEL" />
        </TextButton>
        <Button
          variant="primary"
          size="medium"
          onClick={handleOnSave}
          loading={isUpdating}
          disabled={isSaveButtonDisabled}
        >
          <FormattedMessage id="VIDEO_UPLOAD_SAVE" />
        </Button>
      </Styled.CtaContainer>
    </>
  );
};
